import { CustomWindow } from 'app/@types';
import { ExportFileTypeEnum, MerchantServiceTypeEnum, ServiceTypeEnum } from 'app/models';
import { SpinnerComponent } from '@shared/components/spinner';
import moment from 'moment';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AbstractControl, ValidationErrors } from '@angular/forms';

declare let window: CustomWindow;

export class Utils {
    public static readonly philippinesTimeZone = 'Asia/Manila';
	public static readonly xpressForBusinessDefaultUserId = 8;
	public static readonly connexWalkInDefaultUserId = 14;

    //regex zip code ph and cellnumber regex
    public static readonly zipCodeRegex = /^\d{4}$/;
    public static readonly phMobileNumberRegex = /^(?:\+639|09)\d{9}$/;
    public static readonly mobileNumberRegex = /^\+?\d{10,14}$/;
    public static readonly creditcardaccountRegex =  /^(?=.*[a-zA-Z])[a-zA-Z0-9 ]{1,50}$/;
    public static readonly emailRegex =/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    // Set new ConversationId if needed
    public static checkConversationId(): void {
        // If ConversationId is empty or we have gone from login page
        if (!window.localStorage.getItem('ConversationId') || window['isAfterSignIn']) {
            this.setConversationId();
        }
    }

    // Set new ConversationId
    public static setConversationId(): void {
        // Add to localStorage
        window.localStorage.setItem('ConversationId', window['nextConversationId']);

        // Add to cookie
        const expire = new Date();
        expire.setFullYear(expire.getFullYear() + 100);

        document.cookie = `ConversationId=${window['nextConversationId']}; path=/; expires=${expire.toUTCString()}`;
    }

    // Get ConversationId
    public static getConversationId(): string {
        this.checkConversationId();

        return window.localStorage.getItem('ConversationId') ?? '';
    }

    // Clear stored ConversationId (used in logging)
    public static clearConversationId(): void {
        // Remove from localStorage
        window.localStorage.removeItem('ConversationId');

        // Remove from cookie
        document.cookie = `ConversationId=; path=/; expires=${new Date(0).toUTCString()}`;
    }

    public static getTimezoneOffset(): number {
        const n = new Date().getTimezoneOffset();
        return n / 60 * -1;
    }

    public static newIdempotencyKey(): string {
        if (typeof window.crypto?.randomUUID === 'function') {
            return window.crypto.randomUUID();
        }

        return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    }

	public static isGuestNameBackedUser(userId: number | null | undefined): boolean {
		return userId === Utils.xpressForBusinessDefaultUserId ||
			userId === Utils.connexWalkInDefaultUserId;
	}

	public static getGuestNameBackedUserSource(userId: number | null | undefined): string {
		if (userId === Utils.xpressForBusinessDefaultUserId) {
			return 'X4B';
		}

		if (userId === Utils.connexWalkInDefaultUserId) {
			return 'Walk-in';
		}

		return 'Xpress App';
	}

    // Convert date from UTC to local time
    public static utcToLocalPhilippinesDateString(utcDate: any): string {
        if (!utcDate) {
            return '';
        }
        const dt = new Date(utcDate);

        dt.setHours(dt.getHours() + 8);
        return dt ? dt.toLocaleString() : '';
    }

    // Convert date from UTC to local time
    public static utcToLocalDate(utcDate: any): Date | null {
        if (!utcDate) {
            return null;
        }

        const dt = new Date(utcDate);
        return new Date(dt.getTime() - dt.getTimezoneOffset() * 60 * 1000);
    }

    public static getLocalDate(phDate: Date): Date {
        const result = new Date(phDate);
        result.setHours(result.getHours() - 8 + Utils.getTimezoneOffset());
        return result;
    }

    public static getPHDate(date: Date) {
        const result = new Date(date);
        result.setHours(result.getHours() - Utils.getTimezoneOffset() + 8);
        return result;
    }

    /** Returns today's calendar day in the Philippines/Manila timezone. */
    public static getPHTodayCalendarDate(): Date {
        return this.dateOnlyFromIso(this.formatPHCalendarDateFromInstant(new Date()));
    }

    /** Formats a date-only value as YYYY-MM-DD for APIs that expect a Manila calendar date. */
    public static formatPHCalendarDate(date: Date): string {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /** Formats a date-only value as Manila midnight ISO offset timestamp (YYYY-MM-DDT00:00:00+08:00). */
    public static formatPHMidnightIso(date: Date): string {
        const phOffsetHours = this.getPhilippinesTimezoneHour();
        const phOffset = `+${phOffsetHours.toString().padStart(2, '0')}:00`;
        return `${this.formatPHCalendarDate(date)}T00:00:00${phOffset}`;
    }

    /** Formats an instant as its YYYY-MM-DD calendar day in the Philippines/Manila timezone. */
    public static formatPHCalendarDateFromInstant(date: Date): string {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: this.philippinesTimeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).formatToParts(date);
        const year = parts.find((p) => p.type === 'year')?.value ?? '';
        const month = parts.find((p) => p.type === 'month')?.value ?? '';
        const day = parts.find((p) => p.type === 'day')?.value ?? '';
        return `${year}-${month}-${day}`;
    }

    public static dateOnlyFromIso(isoDate: string): Date {
        const [year, month, day] = isoDate.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    public static normalizeDateOnly(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    public static addCalendarDays(date: Date, days: number): Date {
        const next = this.normalizeDateOnly(date);
        next.setDate(next.getDate() + days);
        return next;
    }

    // Convert date from UTC to local time By TimeZone
    public static utcToLocalDateByTimeZone(utcDate: any, timeZone: number): Date | null {
        if (!utcDate) {
            return null;
        }

        const dt = new Date(utcDate);
        dt.setHours(dt.getHours() + timeZone);
        return dt;
    }

    public static utcToFormatedLocalDateStrNoTime(utcDate: any, format: string = 'DD MMM YYYY'): string {
        const localDateTime = this.utcToLocalDate(utcDate);
        if (!localDateTime) {
            return '';
        }
        const result = moment(localDateTime).format(format);
        return result;
    }

    /**
     * Formats an ISO date string for local display.
     * If the string ends with Z (true UTC instant, e.g. Cosmos), uses moment UTC→local.
     * Otherwise uses utcToFormatedLocalDateStrNoTime (UTC wall time without Z suffix).
     */
    public static utcIsoStringToFormatedLocalDateStr(
        dateStr: string | null | undefined,
        format: string = 'DD MMM YYYY'
    ): string {
        if (!dateStr) {
            return '';
        }
        const trimmed = dateStr.trim();
        if (/z$/i.test(trimmed)) {
            return moment.utc(trimmed).local().format(format);
        }
        return this.utcToFormatedLocalDateStrNoTime(trimmed, format);
    }

    public static utcToFormatedLocalDateStr(utcDate: any, delimeter: string = ' |'): string {
        const localDateTime = this.utcToLocalDate(utcDate);
        if (!localDateTime) {
            return '-';
        }
        const result = moment(localDateTime).format('DD MMM YYYY' + delimeter + ' h:mm A');
        return result;
    }

    public static utcToLocaleString(utcDate: any): string {
        if (!utcDate) {
            return '';
        }

        const localDate = this.utcToLocalDate(utcDate);

        return localDate ? localDate.toLocaleString() : '';
    }

    public static utcToLocaleStringDateFormat(utcDate: any): string {
        const localDateEnd = this.utcToLocaleString(utcDate);
        const dateEnd = new Date(localDateEnd);
        let formattedDate = '';

        if (!isNaN(dateEnd.getTime())) {
            // Array of month names
            const monthNames = [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ];

            // Get the month, day, and year
            const month = monthNames[dateEnd.getMonth()];
            const day = dateEnd.getDate();
            const year = dateEnd.getFullYear();

            // Format the date in "Month Day, Year"
            formattedDate = month + ' ' + day + ', ' + year;

            return formattedDate;
        } else {
            console.error('Invalid date format');
        }

        return formattedDate;
    }

    public static utcToLocaleDateString(utcDate: any): string {
        if (!utcDate) {
            return '';
        }

        const localDate = this.utcToLocalDate(utcDate);

        return localDate ? localDate.toLocaleDateString() : '';
    }

    public static utcToLocaleTimeString(utcDate: any): string {
        if (!utcDate) {
            return '';
        }

        const localDate = this.utcToLocalDate(utcDate);

        return localDate ? localDate.toLocaleTimeString() : '';
    }

    /** Relative label from a local Date (e.g. after utcToLocalDate). */
    public static formatRelativeTimeAgo(localDate: Date | null, now: Date = new Date()): string {
        if (!localDate || Number.isNaN(localDate.getTime())) {
            return '—';
        }

        const sec = Math.floor((now.getTime() - localDate.getTime()) / 1000);
        if (sec < 45) {
            return 'Just now';
        }
        if (sec < 3600) {
            const m = Math.floor(sec / 60);
            return `${m} min ago`;
        }
        if (sec < 86400) {
            const h = Math.floor(sec / 3600);
            return `${h} hrs ago`;
        }

        const days = Math.floor(sec / 86400);
        return `${days} day${days === 1 ? '' : 's'} ago`;
    }

    /** UTC instant from API → relative label (ConneX-style copy). */
    public static formatUtcRelativeTimeAgo(utcDate: any, now: Date = new Date()): string {
        if (!utcDate) {
            return '—';
        }
        const raw = typeof utcDate === 'string' ? utcDate.trim() : utcDate;
        const instant = typeof raw === 'string' && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)
            ? new Date(`${raw}Z`)
            : new Date(raw);
        return this.formatRelativeTimeAgo(Number.isNaN(instant.getTime()) ? null : instant, now);
    }

    public static showSpinner(dialogService: DialogService): DynamicDialogRef {
        return dialogService.open(SpinnerComponent, {
            contentStyle: { padding: '0' },
            closeOnEscape: false,
            closable: false,
            showHeader: false
        });
    }

    public static onlyNumeric(event: KeyboardEvent): boolean {
        const s = String.fromCharCode(event.keyCode);

        if (s === '.') {
            const val = (event.target as HTMLInputElement).value;

            event.returnValue = val.indexOf('.') === -1;
        }
        else if (!(s === '1' || s === '2' || s === '3' || s === '4' || s === '5' || s === '6'
            || s === '7' || s === '8' || s === '9' || s === '0')) {
            event.returnValue = false;
        }

        return event.returnValue;
    }

    public static allTrim(s: string): string {
        if (!s) {
            s = '';
        }

        let sp = 0;
        let ep = -1;

        for (let i = 0; i < s.length; i++) {
            if (s.charCodeAt(i) !== 32 && s.charCodeAt(i) !== 160) {
                sp = i;
                break;
            }
        }

        for (let i = s.length - 1; i >= 0; i--) {
            if (s.charCodeAt(i) !== 32 && s.charCodeAt(i) !== 160) {
                ep = i;
                break;
            }
        }

        return s.substring(sp, ep + 1);
    }

    public static getDefaultMapOptions(): google.maps.MapOptions {
        return {
            center: { lat: 14.5995, lng: 120.9842 }, // Manila
            zoom: 12
        };
    }

    public static toLocalDayOfWeek(utcDayOfWeek: any, utcTime: any) {
        const timeZoneOffset = this.getTimezoneOffset();

        const utcDate = new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(),
          utcTime.getUTCHours(), utcTime.getUTCMinutes(), utcTime.getUTCSeconds()));
        // Set the day of the week in the new UTC date
        utcDate.setUTCDate(utcDate.getUTCDate() - utcDate.getUTCDay() + utcDayOfWeek);
        // Convert the timezone offset from minutes to milliseconds
        const offset = timeZoneOffset * 60 * 1000;
        // Create a new date object with the UTC date plus the offset
        const localDate = new Date(utcDate.getTime() + offset);
        // Get the local day of the week
        const localDayOfWeek = localDate.getDay();

        return localDayOfWeek;
    }

    // Periods are morning, afternoon, evening
    public static getDayPeriod(): string {
        const hour = (new Date()).getHours();

        if (hour < 12) {
            return 'Morning';
        }
        else if (hour >= 12 && hour < 16) {
            return 'Afternoon';
        }
        else {
            return 'Evening';
        }
    }

    public static getUomId(s: string): number {
        const uomMiligrams = 1;
        const uomGrams = 2;
        const uomKilograms = 3;
        const uomMililitres = 4;
        const uomLiters = 5;
        const uomPint = 6;
        const uomEach = 7;
        const uomPack = 8;
        const uomCarton = 9;

        s = this.allTrim(s).toLowerCase();

        if (s === 'g') {
            return uomGrams;
        }
        else if (s === 'kg') {
            return uomKilograms;
        }
        else if (s === 'l') {
            return uomLiters;
        }
        else if (s === 'ml') {
            return uomMililitres;
        }
        else if (s === 'per pack') {
            return uomPack;
        }
        else if (s === 'per carton') {
            return uomCarton;
        }
        else if (s === 'per box') {
            return uomPack;
        }
        else {
            return uomEach;
        }
    }

    public static getFileNameFromResponse(response: any): string {
        const contentDisposition = response.headers.get('Content-Disposition');
        const matches = /filename[^;=\n]*=(([\'"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
            return matches[1].replace(/[\'"]/g, '');
        } else {
            const now = new Date();
            const formattedDate = now.toISOString().replace(/[-:T]/g, '').slice(0, 12);
            return `ImageArchive_${formattedDate}.zip`;
        }
    }

    public static saveFileResponse(data: any, fileName: string, fileType: ExportFileTypeEnum | null = null): void {
        if (!data || !data.ok || !data.body) {
            return;
        }

        let fileExtension = '';
        switch (fileType) {
            case ExportFileTypeEnum.Excel: fileExtension = '.xlsx'; break;
            case ExportFileTypeEnum.PDF: fileExtension = '.pdf'; break;
            case ExportFileTypeEnum.CSV: fileExtension = '.csv'; break;
        }

        const blob: Blob = data.body as Blob;

        const objectUrl = window['URL'].createObjectURL(blob);

        const a = document.createElement('a');
        a.download = fileName + fileExtension;
        a.href = objectUrl;
        a.click();
        a.remove();

        window['URL'].revokeObjectURL(objectUrl);
    }

    public static saveFileFromBase64Response(data: any, fallbackFileName?: string): boolean {
        // Transaction exports now arrive as a normal binary HTTP response. Keep the old
        // base64 branch temporarily for other callers while avoiding atob/byte-array copies
        // for the streamed transaction-export path.
        if (data?.Success && data?.HttpResponse?.ok && data?.HttpResponse?.body) {
            const response = data.HttpResponse;
            const downloadBlob: Blob = response.body as Blob;
            const contentDisposition = response.headers?.get('Content-Disposition');
            const matches = contentDisposition
                ? /filename[^;=\n]*=(([\'"]).*?\2|[^;\n]*)/.exec(contentDisposition)
                : null;
            const responseFileName = matches?.[1]?.replace(/[\'"]/g, '');
            const downloadFileName = responseFileName || fallbackFileName || 'download';
            const downloadContentType = (response.headers?.get('Content-Type') || downloadBlob.type || '').toLowerCase();
            const extension = downloadContentType.includes('spreadsheetml') ? '.xlsx'
                : downloadContentType.includes('pdf') ? '.pdf'
                : downloadContentType.includes('csv') ? '.csv' : '';
            const safeFileName = /\.(csv|xlsx|pdf)$/i.test(downloadFileName) ? downloadFileName : downloadFileName + extension;
            const downloadObjectUrl = window['URL'].createObjectURL(downloadBlob);
            const downloadAnchor = document.createElement('a');
            downloadAnchor.download = safeFileName;
            downloadAnchor.href = downloadObjectUrl;
            downloadAnchor.click();
            downloadAnchor.remove();
            window['URL'].revokeObjectURL(downloadObjectUrl);
            return true;
        }

        if (!data || !data.Success || !data.FileBytes) {
            return false;
        }

        const byteCharacters = atob(data.FileBytes);
        if (byteCharacters.length === 0) {
            return false;
        }

        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        const contentType = data.ContentType || 'application/octet-stream';
        const blob = new Blob([byteArray], { type: contentType });

        const fileName = data.FileName || fallbackFileName || 'download';
        let fileExtension = '';
        if (!fileName.includes('.')) {
            if (contentType.includes('spreadsheetml') || contentType.includes('excel')) {
                fileExtension = '.xlsx';
            } else if (contentType.includes('pdf')) {
                fileExtension = '.pdf';
            } else if (contentType.includes('csv')) {
                fileExtension = '.csv';
            }
        }

        const objectUrl = window['URL'].createObjectURL(blob);

        const a = document.createElement('a');
        a.download = fileName + fileExtension;
        a.href = objectUrl;
        a.click();
        a.remove();

        window['URL'].revokeObjectURL(objectUrl);
        return true;
    }

    public static openPdfInNewTab(data: any): void {
        if (!data || !data.ok || !data.body) {
            return;
        }
        const blob: Blob = data.body as Blob;

        const objectUrl = window['URL'].createObjectURL(blob);

        const newTab = window.open(objectUrl, '_blank');

        if (newTab) {
            newTab.focus();
        }

        window['URL'].revokeObjectURL(objectUrl);
    }

    public static validateEmail(email: string): boolean {
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        return emailPattern.test(email);
    }

    public static getRowsPerPageCount(): number {
        return 15;
    }

    public static isNullOrEmpty(str: string): boolean {
        return str === undefined || str === null || str === '';
    }

    public static getPhilippinesTimezoneHour(): number {
        return 8;
    }

    public static findDuplicatesByPropertyValue<T>(arr: T[], property: keyof T): T[] {
        const duplicates: T[] = [];
        const counts: { [key: string]: number } = {};

        for (const obj of arr) {
            const key = obj[property] as string;
            if (key && counts[key]) {
                if (counts[key] === 1) {
                    duplicates.push(arr.find((item) => item[property] === key) as T);
                }
                duplicates.push(obj);
            }
            counts[key] = (counts[key] || 0) + 1;
        }

        return duplicates;
    }

    public static stringInsert(str: string, value: any, position: number) {
        //return str.slice(0, position) + value + str.slice(position);
        return str.substring(0, position) + value + str.substring(position);
    }

    public static getUploadedFileIcon(fileType: string | undefined) {
        return (fileType === 'application/pdf')
            ? 'Content/xpress-app/images/operator/signup/file-pdf.svg'
            : 'Content/xpress-app/images/operator/signup/file-jpg.svg';
    }

    public static getFileName(file: File) {
        return file?.name;
    }
    public static getServiceTypeLabel(serviceType: number): string {
        switch (serviceType) {
            case MerchantServiceTypeEnum.Delivery:
                return 'Delivery';
            case MerchantServiceTypeEnum.PickUp:
                return 'Pick-up';
            case MerchantServiceTypeEnum.Booking:
                return 'Booking';
            default:
                return 'Unknown Service';
        }
    }

    public static cannotBeOnlyNumbersOrSpaces(control: AbstractControl): ValidationErrors | null {
        const value = control.value || '';
        // all spaces or empty
        const isEmpty = value.trim().length === 0;
         // only digits and spaces
        const isOnlyNumbers = /^[0-9 ]+$/.test(value);
        const hasLetters = /[a-zA-Z]/.test(value);
        if (isEmpty) {
          return { onlySpaces: true };
        } else if (!hasLetters && isOnlyNumbers) {
          return { onlyNumbers: true };
        }
        return null;
    }

    public static toFixedSafe(value: any, decimals: number = 2): string {
        const num = Number(value);
        return isNaN(num) ? '0.00' : num.toFixed(decimals);
    }

    public static isNullOrUndefined(value: any): boolean {
        return value === undefined || value === null;
    }

    public static getEnumValueByName<T extends object>(enumObj: T, key: string) {
        if (key in enumObj) {
            return enumObj[key as keyof T];
        }
        return null;
	}

	public static buildCancelDialogData(
		serviceTypeId: number,
		isBackToBack: boolean
	) {
		const isDayTrip = serviceTypeId === ServiceTypeEnum.DayTrip;

		if (isDayTrip) {
			return {
				title: 'Cancel this Trip?',
				message: `Are you sure you want to cancel this booking?
					This action will notify both the driver and customer immediately.`,
				backLabel: 'Go Back',
				confirmLabel: 'Cancel Trip'
			};
		}

		if (isBackToBack) {
			return {
				title: 'Cancel this Ride?',
				message: `You're about to cancel a back-to-back (secondary) booking.
					This will notify both the driver and the customer. Please select a reason for cancellation to proceed.`,
				backLabel: 'Go Back',
				confirmLabel: 'Confirm Cancellation'
			};
		}

		const message = `You're about to cancel the current booking. This will notify both the driver and the customer.
			Please select a reason for cancellation to proceed.`;

		return {
			title: 'Cancel this Ride?',
			message,
			backLabel: 'Cancel',
			confirmLabel: 'Confirm Cancellation'
		};
	}
}
